// How a ridden bike moves, as plain numbers (bike riding plan 1, 2026-10-18): the bike speeds up and rolls to
// a stop instead of jumping between speeds, the legs pedal at a rate tied to ground speed (and rest on the
// pedals while it rolls), and the wheels roll with it (never seeming to turn backwards). The rider's upper body never moves (owner
// 2026-10-19: rocking looked bad). Pure: the review pages and the game's rider share it.

// Top speeds in px/s: greased, per bike, and with a dry chain on any bike (walking speed). G11 (owner
// 2026-10-24): "too slow" turned out to be a dry pink chain; greased, faster than these was "way too fast".
export const BIKE_SPEED = { pink: 420, blue: 480, dry: 220 };

// What getting on says, if anything: a dry chain holds the bike to walking speed (G11, owner 2026-10-24).
export function mountNote(grease) {
  return grease > 0 ? null : 'Łańcuch suchy {wrench} — nasmaruj go, żeby jechać szybciej';
}

export const RIDE = {
  PX_PER_CRANK_TURN: 280,   // ground px per crank turn: greased pink (420 px/s) pedals 1.5 turns a second
  ACCEL_S: 0.5,             // standing start to top speed
  COAST_S: 0.5,             // top speed to a standstill once you stop pushing
  STOP_FRAME: 8,            // left foot down
  RESTART_FRAME: 5,         // left crank back-down, right crank front-up: the stop pose's pedals
};

export function stepSpeed(speed, target, topSpeed, dtS) {
  if (speed < target) return Math.min(target, speed + topSpeed / RIDE.ACCEL_S * dtS);
  return Math.max(target, Math.max(0, speed - topSpeed / RIDE.COAST_S * dtS));
}

export function advanceCrank(turns, speed, dtS, pedalling) {
  return pedalling ? turns + speed / RIDE.PX_PER_CRANK_TURN * dtS : turns;
}

export function crankFrame(turns) {
  return Math.floor((((turns % 1) + 1) % 1) * 8) % 8;
}

// The art faces left, so rolling forward turns a wheel anticlockwise: the canvas angle falls (the right-facing
// rows mirror the drawing, and its turn with it). Spokes repeat every 2π/spokes, and a step of half that or more
// a frame reads as turning backwards (the wagon-wheel effect), so the drawn step is capped under it: at top
// speed a wheel turns a little slower than it rolls, never the wrong way.
export const WHEEL_MAX_STEP = 0.4;   // of the gap between two spokes, per drawn frame

export function advanceWheel(angleRad, speed, dtS, wheelRadiusPx, spokes) {
  return angleRad - Math.min(speed / wheelRadiusPx * dtS, WHEEL_MAX_STEP * 2 * Math.PI / spokes);
}

export function legsFrame({ speed, pedalling, turns }) {
  return speed === 0 && !pedalling ? RIDE.STOP_FRAME : crankFrame(turns);
}

export function leaveStop() {
  return RIDE.RESTART_FRAME / 8;
}

// The game's per-tick ride state (bike plan 3, task 2), the review page's tick() with the speed handed in:
// st {turns, wheel, stopped, tilt, frame}. Leaving a stop restarts the crank from the stop pose's pedals, the
// crank turns only while pedalling, the wheels roll with the speed, and once stopped (speed 0, not pedalling) the
// foot goes down and the rider eases into the row's stop tilt.
export function rideTick(st, { speed, pedalling, dt, wheelRadiusPx, spokes, stopTilt }) {
  let { turns, stopped } = st;
  if (pedalling && stopped) { turns = leaveStop(); stopped = false; }
  turns = advanceCrank(turns, speed, dt, pedalling);
  const wheel = advanceWheel(st.wheel, speed, dt, wheelRadiusPx, spokes);
  if (speed === 0 && !pedalling) stopped = true;
  const tilt = st.tilt + ((stopped ? stopTilt : 0) - st.tilt) * Math.min(1, dt / 0.15);
  return { turns, wheel, stopped, tilt, frame: legsFrame({ speed, pedalling, turns }) };
}

// The saved ride flag names a bike that is no longer owned (sold on another device while this one was riding
// it): it is stale and must be cleared, or it blocks getting on another bike and the benches for good.
// Kept while the state has not loaded (bikes undefined). bikes is keyed by design id (bike plan 6).
export function staleRide(session, bikes) {
  return !!(session?.riding && bikes && !bikes[session.ridingId]?.owned);
}
